// src/components/Signup.tsx
import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import { downloadResumeTemplate, viewResumeTemplate } from '../utils/resumeTemplate';
import { countryList, getStatesForCountry } from '../utils/countriesStates';
import { validateEmail, validatePhone } from '../utils/validation';
import { getCaptchaToken } from '../utils/captcha';
import MFAEnrollment from './MFAEnrollment';
import '../styles/Signup.css';

// Rotating headline word for the welcome panel (from the first landing design).
const ROTATING = ['find', 'tailor', 'track', 'nail'];

const Signup: React.FC = () => {
  const [word, setWord] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWord((w) => (w + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordStrengthFeedback, setPasswordStrengthFeedback] = useState<{
    strength: string;
    score: number;
    feedback: string[];
  } | null>(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [qualifications, setQualifications] = useState<string[]>([]);
  const [programmeOfStudy, setProgrammeOfStudy] = useState<{ [key: string]: string }>({});
  const [certifications, setCertifications] = useState<string[]>([]);
  const [newCertification, setNewCertification] = useState('');
  const [experienceLevel, setExperienceLevel] = useState<string[]>([]);
  const [jobSearchLocation, setJobSearchLocation] = useState({ country: 'Canada', province: '' });
  const [jobBoard, setJobBoard] = useState('Indeed'); // ← NEW: default to Indeed
  const [consents, setConsents] = useState({
    terms: false,         // inner: Terms of Service
    privacy: false,       // inner: Privacy Policy
    master: false,        // outer: confirms both inner
    dataStorage: false,
    aiReview: false,
    emailAccess: false,
    webCrawl: false,
  });

  // UC-08 A2: any change to inner checkboxes invalidates the master confirmation.
  const handleConsentToggle = (key: 'terms' | 'privacy') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsents((prev) => ({
      ...prev,
      [key]: e.target.checked,
      // Re-uncheck master if either inner was changed
      master: false,
    }));
  };

  const handleMasterToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow checking master if both inners are checked.
    if (e.target.checked && !(consents.terms && consents.privacy)) {
      return;
    }
    setConsents((prev) => ({ ...prev, master: e.target.checked }));
  };
  const [error, setError] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [verificationEmailSentTo, setVerificationEmailSentTo] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showMFAEnrollment, setShowMFAEnrollment] = useState(false);
  const [availableStates, setAvailableStates] = useState(getStatesForCountry('Canada'));

  // Initialize CSRF token before any POST is made
  useEffect(() => {
    axiosInstance.get('/health').catch(() => {});
  }, []);

  // Fetch password strength from backend in real-time
  const handlePasswordChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    if (newPassword.length > 0) {
      try {
        const response = await axiosInstance.post('/auth/check-password-strength', {
          password: newPassword,
        });
        setPasswordStrengthFeedback({
          strength: response.data.strength,
          score: response.data.score,
          feedback: response.data.feedback,
        });
      } catch (err) {
        console.error('Failed to check password strength:', err);
      }
    } else {
      setPasswordStrengthFeedback(null);
    }
  };

  // Helper function - kept for reference but using API feedback instead
  // const passwordStrength = () => {
  //   if (!password) return 'None';
  //   if (password.length < 8) return 'Weak';
  //   if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) return 'Medium';
  //   return 'Strong';
  // };

  const handleQualificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    if (checked) {
      setQualifications([...qualifications, value]);
    } else {
      setQualifications(qualifications.filter(q => q !== value));
      const updated = { ...programmeOfStudy };
      delete updated[value];
      setProgrammeOfStudy(updated);
    }
  };

  const handleProgrammeChange = (qual: string, value: string) => {
    setProgrammeOfStudy(prev => ({ ...prev, [qual]: value }));
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'country') {
      // Update country and fetch available states for that country
      setJobSearchLocation({ country: value, province: '' });
      setAvailableStates(getStatesForCountry(value));
    } else {
      setJobSearchLocation(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setConsents(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const errs: string[] = [];

    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) errs.push(emailCheck.message!);
    const phoneCheck = validatePhone(phone);
    if (!phoneCheck.valid) errs.push(phoneCheck.message!);
    if (!password || password.length < 8) errs.push('Password must be at least 8 characters');
    if (password !== confirmPassword) errs.push('Passwords do not match');
    if (qualifications.length === 0) errs.push('Select at least one qualification');
    if (experienceLevel.length === 0) errs.push('Select experience level');
    if (!jobSearchLocation.country || !jobSearchLocation.province) errs.push('Select job search location');
    if (!resumeFile) errs.push('Upload or create resume');
    if (!consents.terms) errs.push('You must accept the Terms of Service');
    if (!consents.privacy) errs.push('You must accept the Privacy Policy');
    if (!consents.master) errs.push('You must confirm acceptance of both the Privacy Policy and Terms of Service');
    if (!consents.dataStorage) errs.push('Consent to data storage is required');

    // Academic qualifications need a field of study; "Professional Cert" is
    // captured via the certifications list instead.
    qualifications.forEach(q => {
      if (q !== 'Professional Cert' && !programmeOfStudy[q]) errs.push(`Programme of study required for ${q}`);
    });

    setError(errs);
    return errs.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError([]);
    setSuccessMessage('');
    setVerificationEmailSentTo(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('fullName', fullName);
      formData.append('phone', phone);
      formData.append('experienceLevel', experienceLevel.join(','));
      formData.append('jobSearchLocation', JSON.stringify(jobSearchLocation));
      formData.append('jobBoard', jobBoard);
      formData.append('consents', JSON.stringify(consents));
      formData.append('qualifications', JSON.stringify(qualifications));
      formData.append('programmeOfStudy', JSON.stringify(programmeOfStudy));
      formData.append('certifications', JSON.stringify(certifications));

      if (resumeFile) formData.append('resume', resumeFile);

      // Phase 20.1 — no-op until REACT_APP_RECAPTCHA_SITE_KEY is configured
      const captchaToken = await getCaptchaToken('register');
      if (captchaToken) formData.append('captchaToken', captchaToken);

      const regResp = await axiosInstance.post('/auth/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // UC-1.1 A7: surface email-delivery failure with a Resend CTA.
      if (regResp.data?.emailDeliveryFailed) {
        toast.error(
          "We couldn't send the verification email. Click \"Resend\" below to try again.",
          { duration: 7000 }
        );
      } else {
        toast.success(
          '🎉 Welcome to Sumbitrbuddy!\n\nWe\'ve sent a verification link to your email.\nClick the link to activate your account and get started!',
          { duration: 6000 }
        );
      }
      setVerificationEmailSentTo(email);
      setSuccessMessage('sent');

      // Do NOT redirect yet — stay on this page until verified

    } catch (err: any) {
      setError([err.response?.data?.error || 'Registration failed. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmailSentTo) return;

    setResendLoading(true);

    try {
      await axiosInstance.post('/auth/resend-verification', {
        email: verificationEmailSentTo,
      });

      toast.success('Verification email resent! Check your inbox (and spam folder).');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to resend verification email. Try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="hb-signup-container hb-auth-split">
      {/* Welcoming side panel (visual only — form logic unchanged) */}
      <aside className="hb-auth-aside">
        <div className="hb-auth-orbs" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="hb-auth-aside-inner">
          <div className="hb-auth-brand">
            <span className="hb-auth-brand-mark">S</span> Sumbitrbuddy
          </div>
          <h2 className="hb-auth-headline">
            Start landing offers,{" "}
            <span className="hb-auth-accent">not juggling tabs</span>.
          </h2>
          <p className="hb-auth-lead">
            Create your free account and let AI scout, tailor, track, and prep —
            your whole job hunt living in one place.
          </p>
          <ul className="hb-auth-points">
            <li>Scan every board in your region at once</li>
            <li>Tailor your resume to each role in seconds</li>
            <li>Never lose an application again</li>
          </ul>
          <div className="hb-auth-chips">
            <span className="hb-auth-chip">Free to start</span>
            <span className="hb-auth-chip hb-auth-chip-b">No credit card</span>
          </div>

          {/* Write-up + product card carried over from the first landing design */}
          <div className="hb-authx">
            <span className="hb-authx-eyebrow">Your job search, supported</span>
            <h3 className="hb-authx-title">
              Let a buddy help you{" "}
              <span className="hb-authx-rotate" key={word}>{ROTATING[word]}</span>
              <br />your next role.
            </h3>
            <p className="hb-authx-sub">
              Sumbitrbuddy finds jobs that actually fit you, tailors your resume to
              each one, tracks every application, and preps you for the interview —
              all in one place.
            </p>

            <div className="hb-authx-visual" aria-hidden="true">
              <div className="hb-authx-mock hb-authx-float">
                <div className="hb-authx-mock-head">
                  <span className="hb-authx-dot" />
                  <span className="hb-authx-dot" />
                  <span className="hb-authx-dot" />
                  <span className="hb-authx-mock-title">Top match for you</span>
                </div>
                <div className="hb-authx-mock-body">
                  <div className="hb-authx-ring">
                    <svg viewBox="0 0 120 120" className="hb-authx-ring-svg">
                      <circle className="hb-authx-ring-track" cx="60" cy="60" r="52" />
                      <circle className="hb-authx-ring-fill" cx="60" cy="60" r="52" />
                    </svg>
                    <div className="hb-authx-ring-label">
                      <strong>94%</strong>
                      <span>match</span>
                    </div>
                  </div>
                  <div className="hb-authx-mock-meta">
                    <div className="hb-authx-mock-role">Senior Product Designer</div>
                    <div className="hb-authx-mock-co">Northwind · Toronto, ON</div>
                    <div className="hb-authx-mock-tags">
                      <span>Experience</span>
                      <span>Education</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hb-authx-chip hb-authx-chip-1 hb-authx-float-slow">
                📬 Interview invite logged
              </div>
              <div className="hb-authx-chip hb-authx-chip-2 hb-authx-float-slower">
                ✍️ Resume tailored
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className="hb-auth-main">
      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">💼</span>
        <p style={{ color: 'white' }}>Your job search journey starts here. We're with you every step of the way!</p>
      </div>

      <div className="hb-signup-card">
        <div className="hb-signup-header">
          <h2>Join Sumbitrbuddy</h2>
          <p>Create your account to get personalized job recommendations</p>
        </div>

        {successMessage && (
          <div className="hb-signup-success">
            <h3>✨ Almost there!</h3>
            <p>
              We've sent a verification email to <strong>{verificationEmailSentTo}</strong><br />
              Click the link in your email to activate your account.
            </p>
            <p style={{ fontSize: '0.85em', color: 'var(--color-neutral-600)', marginTop: 'var(--spacing-sm)' }}>
              Didn't receive it? Check your spam folder or click the button below.
            </p>
            {verificationEmailSentTo && (
              <div className="hb-signup-success-actions">
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="hb-btn hb-btn-secondary"
                >
                  {resendLoading ? 'Sending...' : '📧 Resend Verification'}
                </button>
                <button
                  onClick={() => setShowMFAEnrollment(true)}
                  className="hb-btn hb-btn-outline"
                >
                  🔐 Enable Two-Factor Auth
                </button>
              </div>
            )}
          </div>
        )}

        {error.length > 0 && (
          <div className="hb-alert hb-alert-error" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <strong>Please fix the following:</strong>
            <ul style={{ marginTop: 'var(--spacing-xs)', paddingLeft: 'var(--spacing-md)' }}>
              {error.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {!successMessage && (
          <form onSubmit={handleSubmit}>
            {/* Account Details */}
            <div className="hb-signup-section">
              <h3 className="hb-signup-section-title">Account Details</h3>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="hb-input"
                  placeholder="you@example.com"
                />
              </div>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="hb-input"
                  placeholder="Create a strong password"
                />
                {passwordStrengthFeedback && (
                  <div className="hb-password-strength">
                    <div className="hb-password-strength-label">
                      <span className="hb-password-strength-text" style={{
                        color: passwordStrengthFeedback.strength === 'Strong'
                          ? 'var(--color-status-success)'
                          : passwordStrengthFeedback.strength === 'Medium'
                            ? 'var(--color-secondary-teal)'
                            : 'var(--color-status-warning)'
                      }}>
                        {passwordStrengthFeedback.strength}
                      </span>
                      <span>{passwordStrengthFeedback.score}%</span>
                    </div>
                    <div className="hb-password-strength-bar">
                      <div
                        className={`hb-password-strength-fill ${passwordStrengthFeedback.strength.toLowerCase()}`}
                        style={{ width: `${passwordStrengthFeedback.score}%` }}
                      />
                    </div>
                    {passwordStrengthFeedback.feedback.length > 0 && (
                      <ul className="hb-password-feedback">
                        {passwordStrengthFeedback.feedback.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {!passwordStrengthFeedback && password.length === 0 && (
                  <span className="hb-signup-helper">
                    Use 8+ characters with uppercase, lowercase, and numbers
                  </span>
                )}
              </div>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  className="hb-input"
                  placeholder="Re-enter your password"
                />
              </div>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="hb-input"
                  placeholder="John Doe"
                />
                <span className="hb-signup-helper">Optional, but helps personalize your experience</span>
              </div>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="hb-input"
                  placeholder="+1 (555) 123-4567"
                />
                <span className="hb-signup-helper">For notifications about applications</span>
              </div>
            </div>

            {/* Resume */}
            <div className="hb-signup-section">
              <h3 className="hb-signup-section-title">Your Resume</h3>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Upload Resume</label>
                <div className="hb-signup-resume-upload">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={e => setResumeFile(e.target.files?.[0] || null)}
                    required
                    className="hb-input"
                  />
                  <span className="hb-signup-helper">PDF, DOCX, DOC, or TXT format</span>
                  <div className="hb-signup-resume-buttons">
                    <button
                      type="button"
                      onClick={() => setShowTemplateModal(true)}
                      className="hb-btn hb-btn-outline hb-btn-sm"
                    >
                      📄 View Template
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadResumeTemplate(fullName || 'Your Name', email, phone)}
                      className="hb-btn hb-btn-outline hb-btn-sm"
                    >
                      ⬇️ Download Template
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Education & Experience */}
            <div className="hb-signup-section">
              <h3 className="hb-signup-section-title">Education & Experience</h3>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Qualifications</label>
                <div className="hb-signup-checkbox-group">
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" value="BSc" onChange={handleQualificationChange} />
                    <span>Bachelor's Degree (BSc)</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" value="MSc" onChange={handleQualificationChange} />
                    <span>Master's Degree (MSc)</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" value="PhD" onChange={handleQualificationChange} />
                    <span>Doctorate (PhD)</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" value="Professional Cert" onChange={handleQualificationChange} />
                    <span>Professional Certification</span>
                  </label>
                </div>
              </div>

              {/* Field of study — academic qualifications only. */}
              {qualifications.filter(q => q !== 'Professional Cert').map(q => (
                <div key={q} className="hb-signup-programme-input">
                  <label className="hb-signup-label hb-signup-label-required">
                    Program for {q}
                  </label>
                  <input
                    type="text"
                    value={programmeOfStudy[q] || ''}
                    onChange={e => handleProgrammeChange(q, e.target.value)}
                    required
                    className="hb-input"
                    placeholder="e.g., Computer Science"
                  />
                </div>
              ))}

              {/* Professional certifications & licenses — feeds job matching. */}
              {qualifications.includes('Professional Cert') && (
                <div className="hb-signup-form-group">
                  <label className="hb-signup-label">Professional Certifications &amp; Licenses</label>
                  <div className="hb-signup-tags">
                    {certifications.map(cert => (
                      <span key={cert} className="hb-signup-tag">
                        {cert}
                        <button type="button" onClick={() => setCertifications(certifications.filter(c => c !== cert))}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={newCertification}
                      onChange={e => setNewCertification(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = newCertification.trim();
                          if (v && !certifications.includes(v)) setCertifications([...certifications, v]);
                          setNewCertification('');
                        }
                      }}
                      className="hb-input"
                      placeholder="e.g., AWS, PMP, CFA — press Enter to add"
                    />
                    <button
                      type="button"
                      className="hb-btn hb-btn-secondary"
                      onClick={() => {
                        const v = newCertification.trim();
                        if (v && !certifications.includes(v)) setCertifications([...certifications, v]);
                        setNewCertification('');
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Experience Level</label>
                <div className="hb-signup-checkbox-group">
                  <label className="hb-signup-checkbox-label">
                    <input type="radio" name="experience" value="Junior" onChange={e => setExperienceLevel([e.target.value])} />
                    <span>Junior (Entry-level or career change)</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="radio" name="experience" value="Intermediate" onChange={e => setExperienceLevel([e.target.value])} />
                    <span>Intermediate (Mid to Senior level)</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="radio" name="experience" value="Advanced" onChange={e => setExperienceLevel([e.target.value])} />
                    <span>Advanced (Expert level)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Job Preferences */}
            <div className="hb-signup-section">
              <h3 className="hb-signup-section-title">Job Preferences</h3>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label hb-signup-label-required">Where are you looking for jobs?</label>
                <div className="hb-signup-location-grid">
                  <div className="hb-signup-location-field">
                    <label className="hb-signup-sublabel">Country</label>
                    <select
                      name="country"
                      value={jobSearchLocation.country}
                      onChange={handleLocationChange}
                      required
                      className="hb-input"
                    >
                      {/* Canada first, then others */}
                      <option key="Canada" value="Canada">Canada</option>
                      {countryList.filter(c => c !== 'Canada').map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hb-signup-location-field">
                    <label className="hb-signup-sublabel">
                      {jobSearchLocation.country === 'Canada' ? 'Province' : jobSearchLocation.country.includes('United') ? 'State' : 'Province / Region'}
                    </label>
                    <select
                      name="province"
                      value={jobSearchLocation.province}
                      onChange={handleLocationChange}
                      required
                      className="hb-input"
                    >
                      <option value="">
                        — Select {jobSearchLocation.country === 'Canada' ? 'Province' : jobSearchLocation.country.includes('United') ? 'State' : 'Province / Region'} —
                      </option>
                      {availableStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="hb-signup-form-group">
                <label className="hb-signup-label">Preferred Job Board</label>
                <select
                  value={jobBoard}
                  onChange={(e) => setJobBoard(e.target.value)}
                  className="hb-input"
                >
                  <option value="adzuna">Adzuna – UK, CA, AU, US</option>
                  <option value="jsearch">JSearch – Indeed, LinkedIn, Glassdoor</option>
                  <option value="remoteok">RemoteOK – Remote Jobs</option>
                  <option value="themuse">The Muse – Company Culture</option>
                  <option value="arbeitnow">Arbeitnow – EU Jobs</option>
                </select>
                <span className="hb-signup-helper">Sets your default board in Advanced Search</span>
              </div>
            </div>

            {/* Consents */}
            <div className="hb-signup-section">
              <h3 className="hb-signup-section-title">Privacy & Permissions</h3>

              <div className="hb-signup-consents">
                <div className="hb-signup-checkbox-group">
                  {/* UC-08: nested ToS + Privacy + Master confirmation */}
                  <div style={{ padding: 'var(--spacing-md)', background: 'var(--color-neutral-50, #f9f9f9)', borderRadius: 8, border: '1px solid var(--color-neutral-200, #e5e7eb)' }}>
                    <p style={{ fontWeight: 600, margin: '0 0 var(--spacing-sm)' }}>Please review and accept:</p>
                    <label className="hb-signup-checkbox-label" style={{ marginLeft: 16 }}>
                      <input type="checkbox" name="privacy" checked={consents.privacy} onChange={handleConsentToggle('privacy')} required />
                      <span>
                        I have read and agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-blue)' }}>Privacy Policy</a>
                      </span>
                    </label>
                    <label className="hb-signup-checkbox-label" style={{ marginLeft: 16 }}>
                      <input type="checkbox" name="terms" checked={consents.terms} onChange={handleConsentToggle('terms')} required />
                      <span>
                        I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-blue)' }}>Terms of Service</a>
                      </span>
                    </label>
                    <label
                      className="hb-signup-checkbox-label"
                      style={{ marginTop: 'var(--spacing-xs)', opacity: consents.terms && consents.privacy ? 1 : 0.55 }}
                      title={consents.terms && consents.privacy ? '' : 'Please read and accept each document above before confirming.'}
                    >
                      <input
                        type="checkbox"
                        name="master"
                        checked={consents.master}
                        onChange={handleMasterToggle}
                        disabled={!(consents.terms && consents.privacy)}
                        required
                      />
                      <span>
                        <strong>I confirm I have read and accept both the Privacy Policy and Terms of Service</strong>{' '}
                        <span style={{ color: 'var(--color-status-warning)' }}>*</span>
                      </span>
                    </label>
                  </div>

                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" name="dataStorage" checked={consents.dataStorage} onChange={handleConsentChange} required />
                    <span>I consent to secure data storage <span style={{ color: 'var(--color-status-warning)' }}>*</span></span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" name="aiReview" checked={consents.aiReview} onChange={handleConsentChange} />
                    <span>
                      Allow AI to review my resume and cover letters
                      <span style={{ display: 'block', fontSize: '0.8em', color: 'var(--color-text-secondary, #6b7280)' }}>
                        Document text is processed by our hosted AI provider (currently Groq, Inc.) over an
                        encrypted connection, solely to generate your suggestions — never to train models.
                        You can revoke this any time in Settings → Privacy.
                      </span>
                    </span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" name="emailAccess" checked={consents.emailAccess} onChange={handleConsentChange} />
                    <span>Connect email for automatic application tracking</span>
                  </label>
                  <label className="hb-signup-checkbox-label">
                    <input type="checkbox" name="webCrawl" checked={consents.webCrawl} onChange={handleConsentChange} />
                    <span>Enable smart job discovery across the web</span>
                  </label>
                </div>
                <p style={{ 
                  fontSize: 'var(--text-xs)', 
                  color: 'var(--color-neutral-600)', 
                  marginTop: 'var(--spacing-sm)',
                  lineHeight: 1.5 
                }}>
                  We respect your privacy. Your data is encrypted and never shared with third parties.
                </p>
              </div>
            </div>

            <div className="hb-signup-actions">
              <button 
                type="submit" 
                disabled={loading} 
                className="hb-btn hb-btn-primary hb-btn-lg"
                style={{ width: '100%' }}
              >
                {loading ? 'Creating your account...' : '🚀 Create Account'}
              </button>
            </div>
          </form>
        )}

        <div className="hb-signup-footer">
          Already have an account? <a href="/login">Log in here</a>
        </div>
      </div>
      </div>

      {/* Resume Template Modal */}
      {showTemplateModal && (
        <div className="hb-modal-overlay">
          <div className="hb-modal-content">
            <div className="hb-modal-header">
              <h3>📄 Professional Resume Template</h3>
              <p>Copy this template, fill in your details, and save as a document.</p>
            </div>

            <div className="hb-modal-body">
              {viewResumeTemplate(fullName || 'Your Name', email, phone)}
            </div>

            <div className="hb-modal-actions">
              <button
                type="button"
                onClick={() => setShowTemplateModal(false)}
                className="hb-btn hb-btn-outline"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  downloadResumeTemplate(fullName || 'Your Name', email, phone);
                  setShowTemplateModal(false);
                }}
                className="hb-btn hb-btn-primary"
              >
                ⬇️ Download Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA Enrollment Modal */}
      <MFAEnrollment
        isOpen={showMFAEnrollment}
        onClose={() => setShowMFAEnrollment(false)}
      />
    </div>
  );
};

export default Signup;